import { useLayoutEffect, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { ValueBlocksScene } from '@/components/three/ValueBlocksScene';
import { SplitLines } from '@/components/SplitLines';
import { CORE_VALUES, PHILOSOPHY } from '@/data/content';
import { usePerfTier } from '@/lib/usePerfTier';

gsap.registerPlugin(ScrollTrigger);

/* Philosophy card variants */
const cardVariants = {
  hidden: { opacity: 0, y: 40, scale: 0.95 },
  visible: (i) => ({
    opacity: 1, y: 0, scale: 1,
    transition: { duration: 0.8, delay: i * 0.12, ease: [0.16, 1, 0.3, 1] },
  }),
};

export const About = () => {
  const sectionRef = useRef(null);
  const pinRef = useRef(null);
  const progressRef = useRef(0);
  const [active, setActive] = useState(0);
  const { isMobile, reduced, dpr } = usePerfTier();

  useLayoutEffect(() => {
    const isShort = window.innerHeight < 780;
    const pin = !isMobile && !reduced && !isShort;
    const ctx = gsap.context(() => {
      ScrollTrigger.create({
        trigger: pinRef.current,
        start: pin ? 'top top' : 'top 75%',
        end: pin ? '+=180%' : 'bottom top',
        pin,
        onUpdate: (self) => {
          progressRef.current = self.progress;
          // Only auto-switch active value index on scroll when pinned (desktop) to prevent layout flickering on mobile
          if (pin) {
            const idx = Math.min(4, Math.floor(self.progress * 5.2));
            setActive((prev) => (prev === idx ? prev : idx));
          }
        },
      });
    }, sectionRef);
    return () => ctx.revert();
  }, [isMobile, reduced]);

  return (
    <section ref={sectionRef} id="about" data-testid="about-section" className="relative bg-void">
      {/* Inline styles for value item hover effects */}
      <style>{`
        .value-item {
          position: relative;
          overflow: hidden;
        }
        .value-item::after {
          content: '';
          position: absolute;
          bottom: 0;
          left: 0;
          height: 2px;
          width: 0;
          background: linear-gradient(90deg, #C9971B, transparent);
          transition: width 0.6s cubic-bezier(0.16, 1, 0.3, 1);
        }
        .value-item.active::after {
          width: 100%;
        }
        .philosophy-card {
          position: relative;
          overflow: hidden;
        }
        .philosophy-card::before {
          content: '';
          position: absolute;
          top: 0;
          left: -100%;
          width: 50%;
          height: 100%;
          background: linear-gradient(90deg, transparent, rgba(201,151,27,0.05), transparent);
          pointer-events: none;
          z-index: 1;
        }
        .philosophy-card:hover::before {
          animation: philo-shimmer 0.7s ease-out forwards;
        }
        @keyframes philo-shimmer {
          to { left: 200%; }
        }
      `}</style>

      <div ref={pinRef} className="min-h-screen flex items-center section-pad py-20 md:py-0">
        <div className="w-full grid grid-cols-1 lg:grid-cols-2 gap-14 lg:gap-20 items-center">
          {/* Left: manifesto text */}
          <div>
            <motion.p
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
              className="text-[11px] uppercase tracking-[0.4em] text-brand-gold mb-6"
            >
              01 — About Us
            </motion.p>

            <h2 className="font-clash font-semibold text-ink tracking-tight leading-[1.05] text-4xl md:text-5xl">
              <SplitLines inView lines={['Creating Value Through', <span key="r" className="text-ink/70">Responsible Development</span>]} />
            </h2>

            <motion.p
              initial={{ opacity: 0, y: 18 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.9, delay: 0.2 }}
              className="mt-7 text-sm md:text-base text-ink/70 leading-relaxed max-w-lg"
            >
              For decades we have shaped Hyderabad's real estate landscape — from
              large-scale plotted layouts to premium apartments, villa communities and
              commercial developments. Five core values anchor every acre we touch.
            </motion.p>

            {/* Core Values List - Clickable for Instant Focus */}
            <div className="mt-10 space-y-1" data-testid="core-values-list">
              {CORE_VALUES.map((v, i) => (
                <motion.div
                  key={v.name}
                  data-testid={`core-value-${i}`}
                  onClick={() => setActive(i)}
                  initial={{ opacity: 0, x: -30 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.7, delay: 0.3 + i * 0.08, ease: [0.16, 1, 0.3, 1] }}
                  className={`value-item flex items-baseline gap-5 border-b border-ink/10 py-4 transition-all duration-500 cursor-pointer ${
                    active === i ? 'active border-brand-gold/40 pl-2 bg-brand-gold/5 rounded-lg' : 'hover:pl-1'
                  }`}
                >
                  <motion.span
                    animate={{ color: active === i ? '#C9971B' : 'rgba(22,32,62,0.35)', scale: active === i ? 1.15 : 1 }}
                    transition={{ duration: 0.4 }}
                    className="font-clash font-semibold text-sm w-8"
                  >
                    0{i + 1}
                  </motion.span>
                  <div className="flex-1">
                    <motion.p
                      animate={{ color: active === i ? '#16203E' : 'rgba(22,32,62,0.65)' }}
                      transition={{ duration: 0.4 }}
                      className="font-clash font-semibold text-xl md:text-2xl"
                    >
                      {v.name}
                    </motion.p>
                    <div className={`grid transition-[grid-template-rows,opacity,margin] duration-300 ease-out ${active === i ? 'grid-rows-[1fr] opacity-100 mt-1.5' : 'grid-rows-[0fr] opacity-0 mt-0'}`}>
                      <div className="overflow-hidden">
                        <p className="text-xs md:text-sm text-ink/70 leading-relaxed pb-1">
                          {v.desc}
                        </p>
                      </div>
                    </div>
                  </div>
                  {/* Active indicator dot */}
                  <motion.div
                    animate={{ scale: active === i ? 1.2 : 0, opacity: active === i ? 1 : 0 }}
                    transition={{ duration: 0.3, type: 'spring', stiffness: 300 }}
                    className="w-2.5 h-2.5 rounded-full bg-brand-gold shadow-[0_0_10px_#C9971B] flex-shrink-0"
                  />
                </motion.div>
              ))}
            </div>
          </div>

          {/* Right: 3D Viewport — Interactive Architectural Masterplan */}
          <motion.div
            initial={{ opacity: 0, scale: 0.94 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 1.1, delay: 0.25, ease: [0.16, 1, 0.3, 1] }}
            className="relative h-[440px] md:h-[560px] rounded-3xl overflow-hidden border border-ink/15 bg-gradient-to-b from-[#FAFBFD] via-[#F3F6FA] to-[#E9EFF7]"
            style={{ boxShadow: '0 25px 60px rgba(30,58,123,0.12), 0 0 50px rgba(201,151,27,0.08)' }}
          >
            <ValueBlocksScene progressRef={progressRef} activeIndex={active} dpr={dpr} />
            
            {/* Top HUD status bar with value switchers */}
            <div className="absolute top-4 inset-x-5 pointer-events-auto flex items-center justify-between z-10">
              <div className="flex items-center gap-2 bg-white/90 md:glass px-3.5 py-1.5 rounded-full border border-ink/10 shadow-sm">
                <span className="w-2 h-2 rounded-full bg-brand-gold animate-pulse shadow-[0_0_8px_#C9971B]" />
                <span className="text-[10px] uppercase tracking-[0.25em] font-mono text-ink/80 font-medium">Interactive 3D Masterplan</span>
              </div>
              
              {/* Value Selector Dots / Pills */}
              <div className="flex items-center gap-1.5 bg-white/80 md:glass p-1 rounded-full border border-ink/10">
                {CORE_VALUES.map((_, idx) => (
                  <button
                    key={`pill-${idx}`}
                    onClick={() => setActive(idx)}
                    className={`px-2 py-0.5 text-[10px] font-mono rounded-full transition-all duration-300 ${
                      active === idx
                        ? 'bg-brand-gold text-white font-bold shadow-sm'
                        : 'text-ink/60 hover:text-ink hover:bg-ink/5'
                    }`}
                  >
                    0{idx + 1}
                  </button>
                ))}
              </div>
            </div>

            {/* Bottom HUD: Active value title badge */}
            <div className="absolute bottom-5 left-5 right-5 pointer-events-none flex items-end justify-between z-10">
              <div className="bg-white/95 md:glass px-4 sm:px-6 py-3 rounded-2xl border border-ink/15 shadow-lg backdrop-blur-md">
                <p className="text-[9px] uppercase tracking-[0.35em] text-ink/50 font-mono">Current Focus</p>
                <AnimatePresence mode="wait">
                  <motion.p
                    key={active}
                    initial={{ y: 8, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    exit={{ y: -8, opacity: 0 }}
                    transition={{ duration: 0.3 }}
                    className="font-clash font-semibold text-base sm:text-lg md:text-xl text-brand-gold mt-0.5"
                  >
                    0{active + 1} — {CORE_VALUES[active].name}
                  </motion.p>
                </AnimatePresence>
              </div>

              <div className="hidden sm:flex items-center gap-2 glass px-3.5 py-1.5 rounded-full border border-ink/10 shadow-sm">
                <span className="text-[9px] uppercase tracking-[0.25em] font-mono text-ink/60 font-medium">Scroll / Click to Explore</span>
              </div>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Philosophy strip */}
      <div className="section-pad pb-28 md:pb-40 pt-8">
        <motion.p
          initial={{ opacity: 0, y: 12 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="text-[11px] uppercase tracking-[0.4em] text-ink/55 mb-10 font-medium"
        >
          Our philosophy is simple
        </motion.p>
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-5" data-testid="philosophy-grid">
          {PHILOSOPHY.map((p, i) => (
            <motion.div
              key={p.step}
              custom={i}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: '-8%' }}
              variants={cardVariants}
              whileHover={{ y: -8, scale: 1.03, transition: { duration: 0.35 } }}
              className="philosophy-card glass rounded-2xl p-7 group hover:border-brand-gold/40 transition-all duration-500 cursor-default"
            >
              <motion.span
                whileHover={{ scale: 1.2 }}
                className="font-clash text-3xl text-ink/35 group-hover:text-brand-gold transition-colors duration-500 inline-block font-semibold"
              >
                {p.step}
              </motion.span>
              <h3 className="mt-6 font-clash font-semibold text-lg text-ink">{p.title}</h3>
              <p className="mt-2 text-xs md:text-sm text-ink/70 leading-relaxed">{p.desc}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};
